import { useId, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ForgeButton } from './ForgeButton'
import { ForgeDialog } from './ForgeDialog'
import { Icon } from './Icon'
import { SkeletonRows } from './States'
import { TablePagination } from './TablePagination'
import { TierGlyph } from './TierGlyph'
import { Mono } from './primitives'
import { LINK_PAGE_SIZE } from '@/features/links/api'
import { useLinkPage } from '@/features/links/queries'
import { cn } from '@/lib/cn'

/**
 * Choose the work orders, log book entries or anomalies a record links.
 *
 * ── The place dialog's shape, on purpose ─────────────────────────────────
 * Laid out as `HierarchyPicker` is — where you stand at the top, a search the
 * server answers, a paged list underneath with a control per row, and the
 * footer that confirms — so choosing what to link reads as the same act as
 * choosing where. It replaced a checkbox list inline in the handover form that
 * read one page of 200, and the log book's selects that offered the whole
 * estate's open anomalies and work orders, most of which the save refused.
 *
 * ── The place is the record's, and is not chosen here ────────────────────
 * The server takes a link only at EXACTLY the record's place
 * (`_same_location`), so the dialog stands at that place rather than offering a
 * drill that could only lead to rows the save refuses. Changing it is the
 * form's place field, which clears the links.
 *
 * ── Rows beneath are listed, disabled ────────────────────────────────────
 * See `features/links/api.js`. They stay listed, greyed, with the place they
 * sit at, and the reason is said once above the list.
 *
 * ── `single`, for a one-link column ──────────────────────────────────────
 * A log book entry has one `anomaly_id` and one `work_order_id`. Rows become
 * radios and choosing closes the dialog, as `HierarchyPicker`'s single mode
 * does: the choice is the confirmation.
 *
 * @param {object} props
 * @param {'workOrders'|'logbookEntries'|'anomalies'} props.kind
 * @param {{id: string, tier: string, name: string, trail?: Array}} props.place  As `PlaceField` holds it.
 * @param {{plantId: ?string, lineId: ?string, machineId: ?string}} props.trio
 * @param {Array<{id: string, serialNumber: string, title: string}>} props.value
 * @param {(items: Array) => void} props.onChange
 * @param {() => void} props.onClose
 * @param {boolean} [props.single]
 * @param {number} [props.max]  The most that may be chosen; rows beyond it disable.
 */
export function LinkPickerDialog({ kind, place, trio, value, onChange, onClose, single = false, max }) {
  const { t } = useTranslation(['app', 'common'])
  const searchId = useId()

  const [search, setSearch] = useState('')
  const [pageSize, setPageSize] = useState(LINK_PAGE_SIZE)
  /* Stamped with the search it was set for, as `HierarchyPicker` does, so a new
     search reads as page one with no second update to race. */
  const [pageState, setPageState] = useState({ search: '', value: 1 })
  const page = pageState.search === search ? pageState.value : 1
  const setPage = (next) => setPageState({ search, value: next })

  const list = useLinkPage(kind, trio, { search, page, pageSize })
  const items = list.data?.items ?? []

  const chosen = useMemo(() => new Set(value.map((item) => item.id)), [value])
  const full = !single && max != null && value.length >= max

  const pick = (item) => {
    const link = { id: item.id, serialNumber: item.serialNumber, title: item.title }
    if (single) {
      onChange([link])
      onClose()
      return
    }
    onChange(chosen.has(item.id) ? value.filter((one) => one.id !== item.id) : [...value, link])
  }

  const parent = place.trail?.at(-1)
  const parentName = parent && parent.tier !== 'enterprise' ? parent.name : null
  const label = t(`linkPicker.kinds.${kind}`)

  return (
    <ForgeDialog
      open
      onOpenChange={(next) => !next && onClose()}
      title={t(single ? `linkPicker.chooseOne.${kind}` : `linkPicker.choose.${kind}`)}
      size="lg"
      bodyClassName="p-0"
      footer={
        single ? (
          <ForgeButton onClick={onClose}>{t('common:actions.cancel')}</ForgeButton>
        ) : (
          <>
            <p className="me-auto m-0 text-body-sm text-ink-dim">
              {t('settings.users.picker.chosen', { count: value.length })}
            </p>
            <ForgeButton variant="primary" onClick={onClose}>
              {t('common:actions.done')}
            </ForgeButton>
          </>
        )
      }
    >
      <div className="flex flex-col">
        <div className="border-b border-subtle px-3.5 py-3">
          <p className="mb-2.5 mt-0 flex items-center gap-1.5 text-body-sm">
            <TierGlyph tier={place.tier} bare />
            <span className="font-semibold text-ink">{place.name}</span>
            {parentName ? <span className="text-ink-dim">· {parentName}</span> : null}
          </p>

          <div className="flex items-center gap-2 rounded-sm border border-hairline bg-card px-3 py-2 focus-within:border-forge">
            <Icon name="search" size="sm" className="text-ink-mute" />
            <label className="sr-only" htmlFor={searchId}>
              {t('linkPicker.searchLabel', { label })}
            </label>
            <input
              id={searchId}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={t('linkPicker.searchPlaceholder')}
              className="min-w-0 flex-1 bg-transparent text-body-sm text-ink outline-none placeholder:text-ink-mute"
            />
            {search ? (
              <button
                type="button"
                onClick={() => setSearch('')}
                className="text-micro text-ink-dim underline decoration-from-font"
              >
                {t('common:actions.clear')}
              </button>
            ) : null}
          </div>

          {place.tier !== 'asset' ? (
            <p className="mt-2 mb-0 text-micro text-ink-mute">{t('linkPicker.exactNote')}</p>
          ) : null}
          {full ? (
            <p className="mt-2 mb-0 text-micro text-on-amber">{t('linkPicker.full', { count: max })}</p>
          ) : null}
        </div>

        <div className="max-h-[48vh] min-h-[12rem] overflow-y-auto">
          {list.isPending ? (
            <div className="px-3.5 py-3">
              <SkeletonRows count={5} />
            </div>
          ) : list.isError ? (
            <p className="m-0 px-3.5 py-6 text-center text-body-sm text-on-red">{t('linkPicker.failed')}</p>
          ) : !items.length ? (
            <p className="m-0 px-3.5 py-6 text-center text-body-sm text-ink-dim">
              {search ? t('settings.users.picker.noMatches') : t(`linkPicker.none.${kind}`)}
            </p>
          ) : (
            <ul className="m-0 list-none p-0">
              {items.map((item) => (
                <li key={item.id}>
                  <Row
                    item={item}
                    single={single}
                    selected={chosen.has(item.id)}
                    selectable={item.exact && (chosen.has(item.id) || !full)}
                    onPick={() => pick(item)}
                  />
                </li>
              ))}
            </ul>
          )}
        </div>

        {list.data ? (
          <TablePagination
            total={list.data.total}
            page={page}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={(size) => {
              setPageSize(size)
              setPage(1)
            }}
            label={label.toLowerCase()}
          />
        ) : null}
      </div>
    </ForgeDialog>
  )
}

/** One row: a control, the title, and — when it cannot be linked — where it sits. */
function Row({ item, single, selected, selectable, onPick }) {
  const { t } = useTranslation('app')

  return (
    <label
      className={cn(
        'flex items-center gap-2.5 border-b border-subtle px-3.5 py-2.5',
        // The earned 3px leading edge: it marks a chosen item and nothing else.
        'border-s-[3px] ps-[11px]',
        selected ? 'border-s-forge' : 'border-s-transparent',
        selectable ? 'cursor-pointer hover:bg-sunken' : 'cursor-not-allowed opacity-60',
      )}
    >
      {/* `onClick` for the radio, as in `HierarchyPicker`: pressing the one
          already chosen must still choose it and close. */}
      <input
        type={single ? 'radio' : 'checkbox'}
        name={single ? 'link-picker-item' : undefined}
        className="shrink-0 accent-forge"
        checked={selected}
        disabled={!selectable}
        onChange={single ? () => {} : onPick}
        onClick={single ? onPick : undefined}
      />
      <span className="min-w-0 flex-1">
        <span className={cn('block truncate text-body-sm text-ink', selected && 'font-semibold')}>
          {item.title}
        </span>
        {!item.exact && item.where ? (
          <span className="eyebrow mt-px block truncate">{t('linkPicker.elsewhere', { place: item.where })}</span>
        ) : null}
      </span>
      <Mono className="shrink-0 text-ink-dim">{item.serialNumber}</Mono>
    </label>
  )
}
